import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChevronDown, X, ArrowUp, Loader2, AlertCircle } from "lucide-react";
import socket from "../../socket";
import chatApi from "../../api/chatApi";
import { useToast } from "../../context/ToastContext";

const FloatingChatBox = ({
  chat,
  onClose,
  onToggleCollapse,
  unreadCount = 0,
  isProfessionalView = false,
}) => {
  const { proposal, proposalId } = chat;
  const effectiveProposalId = proposalId || proposal?._id;
  const { showError, showWarning } = useToast();

  const [isCollapsed, setIsCollapsed] = useState(Boolean(chat.isCollapsed));
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [canSend, setCanSend] = useState(true);
  const [statusNotice, setStatusNotice] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isOtherUserOnline, setIsOtherUserOnline] = useState(false);
  const [otherUserInfo, setOtherUserInfo] = useState({ name: null, avatar: null });
  const [resolvedEventName, setResolvedEventName] = useState(
    chat.eventName ||
    proposal?.eventName ||
    proposal?.eventId?.eventName ||
    proposal?.eventId?.title ||
    null
  );
  const [avatarError, setAvatarError] = useState(false);
  const [sending, setSending] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const currentRoomIdRef = useRef(null);
  const otherUserIdRef = useRef(null);
  const isCollapsedRef = useRef(isCollapsed);

  useEffect(() => {
    isCollapsedRef.current = isCollapsed;
  }, [isCollapsed]);

  // Synchronize internal state if parent chat.isCollapsed changes
  useEffect(() => {
    if (typeof chat.isCollapsed === "boolean") {
      setIsCollapsed(chat.isCollapsed);
    }
  }, [chat.isCollapsed]);

  const handleCollapse = (e) => {
    if (e) e.stopPropagation();
    setIsCollapsed(true);
    if (onToggleCollapse) onToggleCollapse(true);
  };

  const handleExpand = (e) => {
    if (e) e.stopPropagation();
    setIsCollapsed(false);
    if (onToggleCollapse) onToggleCollapse(false);
  };

  const currentUserId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("role");

  // Determine if viewing as professional
  const isProfessional = Boolean(
    isProfessionalView ||
    chat.isProfessionalView ||
    proposal?.isProfessionalView ||
    (userRole && userRole !== "customer" && userRole !== "admin")
  );

  // In professional view, the other participant is the Host
  const hostResolvedName =
    otherUserInfo.name ||
    proposal?.hostName ||
    proposal?.eventId?.customer?.userId?.fullName ||
    proposal?.eventId?.customer?.name ||
    "Host";

  const professionalResolvedName =
    otherUserInfo.name ||
    proposal?.professionalName ||
    proposal?.companyName ||
    proposal?.plannerName ||
    proposal?.managerId?.fullName ||
    "Professional";

  const displayName = isProfessional ? hostResolvedName : professionalResolvedName;

  const rawAvatar = isProfessional
    ? otherUserInfo.avatar || proposal?.hostAvatar || null
    : otherUserInfo.avatar ||
      proposal?.professionalAvatar ||
      proposal?.profilePhoto ||
      proposal?.managerId?.profilePicture ||
      proposal?.avatar ||
      null;

  const displayAvatar =
    typeof rawAvatar === "object" && rawAvatar !== null
      ? rawAvatar.secure_url || rawAvatar.url || null
      : rawAvatar;

  const displayEventName =
    resolvedEventName ||
    chat.eventName ||
    proposal?.eventName ||
    proposal?.eventId?.eventName ||
    proposal?.eventId?.title ||
    room?.eventId?.eventName ||
    "Event";

  const scrollToBottom = useCallback((behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  // 1. Initialize Room & Fetch History
  useEffect(() => {
    if (!effectiveProposalId) return;

    let isMounted = true;
    setLoading(true);

    const initChat = async () => {
      try {
        const { data } = await chatApi.getOrCreateProposalRoom(effectiveProposalId);
        if (!isMounted) return;

        setRoom(data.room);
        currentRoomIdRef.current = data.room._id;
        setCanSend(data.canSend);
        setStatusNotice(data.statusNotice);

        if (data.otherUserId) {
          otherUserIdRef.current = data.otherUserId;
        }

        if (data.otherUserName || data.otherUserAvatar) {
          setOtherUserInfo({
            name: data.otherUserName || null,
            avatar: data.otherUserAvatar || null,
          });
        }

        if (data.eventName) {
          setResolvedEventName(data.eventName);
        }

        // Fetch paginated messages
        const msgRes = await chatApi.getRoomMessages(data.room._id, { limit: 50 });
        if (!isMounted) return;
        setMessages(msgRes.data.messages || []);

        // Join room channel via socket
        socket.emit("join_room", { roomId: data.room._id }, (ack) => {
          if (!isMounted) return;
          if (ack?.success) {
            if (ack.otherUserId) otherUserIdRef.current = ack.otherUserId;
            if (ack.otherUserName || ack.otherUserAvatar) {
              setOtherUserInfo((prev) => ({
                name: ack.otherUserName || prev.name,
                avatar: ack.otherUserAvatar || prev.avatar,
              }));
            }
            if (typeof ack.isOtherUserOnline === "boolean") {
              setIsOtherUserOnline(ack.isOtherUserOnline);
            }
            if (ack.eventName) {
              setResolvedEventName(ack.eventName);
            }
          }
        });

        setTimeout(() => scrollToBottom("auto"), 100);
      } catch (err) {
        console.error("Failed to init floating chat:", err);
        if (isMounted) {
          setStatusNotice(err.response?.data?.message || "Failed to load chat.");
          setCanSend(false);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initChat();

    // 2. Real-time Listeners
    const handleNewMessage = (msg) => {
      if (msg.roomId === currentRoomIdRef.current) {
        setMessages((prev) => {
          if (prev.some((m) => m._id === msg._id)) return prev;
          return [...prev, msg];
        });

        // Only mark as read if chat is expanded & receiver is viewing
        if (!isCollapsedRef.current && msg.senderId !== currentUserId) {
          socket.emit("mark_read", { roomId: currentRoomIdRef.current });
        }

        setTimeout(() => scrollToBottom("smooth"), 50);
      }
    };

    const handleUserTyping = ({ roomId }) => {
      if (roomId === currentRoomIdRef.current) setIsTyping(true);
    };

    const handleUserStopTyping = ({ roomId }) => {
      if (roomId === currentRoomIdRef.current) setIsTyping(false);
    };

    const handleUserPresence = ({ userId: targetUserId, isOnline }) => {
      if (targetUserId && String(targetUserId) === String(otherUserIdRef.current)) {
        setIsOtherUserOnline(Boolean(isOnline));
      }
    };

    const handleRoomPresence = ({ otherUserId: targetUserId, otherUserName, otherUserAvatar, eventName, isOnline }) => {
      if (targetUserId) {
        otherUserIdRef.current = targetUserId;
        setIsOtherUserOnline(Boolean(isOnline));
        if (otherUserName || otherUserAvatar) {
          setOtherUserInfo((prev) => ({
            name: otherUserName || prev.name,
            avatar: otherUserAvatar || prev.avatar,
          }));
        }
        if (eventName) {
          setResolvedEventName(eventName);
        }
      }
    };

    socket.on("new_message", handleNewMessage);
    socket.on("user_typing", handleUserTyping);
    socket.on("user_stop_typing", handleUserStopTyping);
    socket.on("user_presence", handleUserPresence);
    socket.on("room_presence", handleRoomPresence);

    return () => {
      isMounted = false;
      if (currentRoomIdRef.current) {
        socket.emit("leave_room", { roomId: currentRoomIdRef.current });
      }
      socket.off("new_message", handleNewMessage);
      socket.off("user_typing", handleUserTyping);
      socket.off("user_stop_typing", handleUserStopTyping);
      socket.off("user_presence", handleUserPresence);
      socket.off("room_presence", handleRoomPresence);
    };
  }, [effectiveProposalId, currentUserId, scrollToBottom]);

  // When expanding from collapsed state, mark unread messages as read
  useEffect(() => {
    if (!isCollapsed && currentRoomIdRef.current) {
      socket.emit("mark_read", { roomId: currentRoomIdRef.current });
      setTimeout(() => scrollToBottom("auto"), 50);
    }
  }, [isCollapsed, scrollToBottom]);

  const handleInputChange = (e) => {
    setText(e.target.value);
    if (!room?._id) return;

    socket.emit("typing", { roomId: room._id });

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit("stop_typing", { roomId: room._id });
    }, 2000);
  };

  const handleSend = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || !room?._id || !canSend || sending) return;

    setSending(true);
    setText("");
    socket.emit("stop_typing", { roomId: room._id });

    socket.emit(
      "send_message",
      { roomId: room._id, message: trimmed },
      (ack) => {
        setSending(false);
        if (!ack || !ack.success) {
          if (showError) {
            showError(ack?.error || "Failed to send message.");
          } else {
            alert(ack?.error || "Failed to send message.");
          }
        } else if (ack.isRedacted && ack.notice) {
          if (showWarning) {
            showWarning(ack.notice);
          }
        }
      }
    );
  };

  return (
    <div
      className={`w-[calc(100vw-24px)] sm:w-[340px] max-w-[340px] shrink-0 bg-white rounded-t-2xl border border-gray-200 border-b-0 shadow-[0_-4px_24px_rgba(0,0,0,0.14)] flex flex-col overflow-hidden pointer-events-auto transition-all duration-200 ease-in-out ${
        isCollapsed ? "h-[58px]" : "h-[450px] sm:h-[470px]"
      }`}
    >
      {/* Header */}
      <div
        onClick={isCollapsed ? handleExpand : undefined}
        className={`px-3.5 py-2.5 bg-white border-b border-gray-100 flex items-center justify-between z-10 shrink-0 select-none overflow-hidden ${
          isCollapsed ? "cursor-pointer hover:bg-gray-50/80 transition-colors" : ""
        }`}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1 overflow-hidden mr-1">
          <div className="relative shrink-0">
            {displayAvatar && !avatarError ? (
              <img
                src={typeof displayAvatar === "object" ? (displayAvatar.secure_url || displayAvatar.url) : displayAvatar}
                alt={displayName}
                onError={() => setAvatarError(true)}
                className="w-10 h-10 rounded-full object-cover border border-orange-100 shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-[#FF4D12] font-bold text-sm shrink-0">
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
            {/* Green dot on profile image to track online status */}
            {isOtherUserOnline && (
              <span
                className="w-3 h-3 bg-green-500 border-2 border-white rounded-full absolute bottom-0 right-0 shadow-xs"
                title="Online"
              />
            )}
          </div>

          <div className="min-w-0 flex-1 overflow-hidden">
            <h3
              className="font-bold text-gray-900 text-[14px] sm:text-[15px] truncate leading-tight block"
              title={displayName}
            >
              {displayName}
            </h3>
            {/* Event Name with automatic ellipsis */}
            <p
              className="text-[11.5px] sm:text-[12px] text-gray-500 truncate leading-tight mt-0.5 block"
              title={displayEventName}
            >
              {isTyping ? (
                <span className="text-[#FF4D12] font-semibold animate-pulse">typing...</span>
              ) : (
                displayEventName
              )}
            </p>
          </div>
        </div>

        {/* Right header actions */}
        <div className="flex items-center gap-1.5 shrink-0 ml-1">
          {isCollapsed ? (
            /* When collapsed: show unread badge and ONLY the Cross button */
            <>
              {unreadCount > 0 && (
                <span className="bg-[#FF4D12] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center shadow-xs">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="p-1 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded-md transition-colors"
                title="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          ) : (
            /* When expanded: show ONLY the Collapse button */
            <button
              type="button"
              onClick={handleCollapse}
              className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              title="Collapse chat"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Main Body & Footer (Hidden when collapsed) */}
      {!isCollapsed && (
        <>
          {/* Messages Container with decreased width scrollbar */}
          <div
            ref={messagesContainerRef}
            className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3 bg-white [scrollbar-width:thin] [scrollbar-color:#d1d5db_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400"
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-[#FF4D12]" />
                <span className="text-xs">Loading...</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 px-4 py-8">
                <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-[#FF4D12] mb-2 text-base">
                  💬
                </div>
                <p className="text-xs font-semibold text-gray-700 mb-0.5">Start the conversation</p>
                <p className="text-[11px] text-gray-400">
                  Discuss details and requirements with {displayName}.
                </p>
              </div>
            ) : (
              messages.map((msg, index) => {
                const senderIdStr =
                  typeof msg.senderId === "object" ? msg.senderId?._id : msg.senderId;
                const isMe = String(senderIdStr) === String(currentUserId);

                const timeStr = msg.createdAt
                  ? new Date(msg.createdAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "";

                return (
                  <div
                    key={msg._id || index}
                    className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}
                  >
                    {/* Top Sender Label matching LinkedIn style */}
                    <span className="text-[10px] text-gray-400 mb-0.5 px-1">
                      {isMe ? "You" : displayName}
                    </span>

                    {/* Speech Bubble */}
                    <div
                      className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed shadow-xs relative ${
                        isMe
                          ? "bg-[#FF4D12] text-white rounded-br-xs"
                          : "bg-[#FFE5DC] text-[#C03600] rounded-bl-xs"
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words">{msg.message}</p>
                    </div>

                    {/* Bottom Timestamp */}
                    <span className="text-[10px] text-gray-400 mt-1 px-1">
                      {timeStr}
                    </span>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Status Notice (if not allowed to send) */}
          {statusNotice && (
            <div className="px-3 py-2 bg-amber-50 border-t border-amber-200 text-amber-800 text-[11px] flex items-center gap-1.5 shrink-0">
              <AlertCircle size={14} className="shrink-0 text-amber-600" />
              <span className="leading-tight">{statusNotice}</span>
            </div>
          )}

          {/* Footer Input Area without attachment icon */}
          <div className="p-3 bg-white border-t border-gray-100 shrink-0">
            {canSend ? (
              <form
                onSubmit={handleSend}
                className="border border-gray-300 rounded-2xl px-3 py-1.5 flex items-center gap-2 bg-white focus-within:border-[#FF4D12] transition-colors"
              >
                <input
                  type="text"
                  value={text}
                  onChange={handleInputChange}
                  placeholder="Write a message..."
                  maxLength={2000}
                  className="flex-1 bg-transparent text-sm text-gray-800 placeholder:text-gray-400 outline-none min-w-0"
                />

                {/* Orange circular Send Button with ArrowUp */}
                <button
                  type="submit"
                  disabled={!text.trim() || sending}
                  className="w-7 h-7 rounded-full bg-[#FF4D12] text-white flex items-center justify-center hover:bg-[#e64510] disabled:opacity-40 transition-all active:scale-95 shrink-0 shadow-xs"
                >
                  {sending ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <ArrowUp size={14} strokeWidth={2.5} />
                  )}
                </button>
              </form>
            ) : (
              <div className="py-2 text-center text-xs text-gray-400 font-medium">
                Chat is closed for this proposal.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FloatingChatBox;
